import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';

import { InteractiveClickOrDrag } from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag';
import {
  CANCELLED,
  DragItem,
  INTERACTIVE_CANVAS_ID,
  INTERACTIVE_CONTAINER_ID,
  INTERACTIVE_DRAG_DROP,
  PLACED,
} from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.constants';
import { isEveryDraggableDropped } from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.utils';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import { useSortableScrollManager } from 'approot/shared/sortable/sortable.hooks';
import { InteractiveResponseHelpControls } from '../../shared/interactive-response-controls';
import {
  InteractiveResponseScreenReader,
  InteractiveResponseTitle,
} from '../../shared/interactive-response-shared';
import {
  useInteractiveAudio,
  useInteractiveClickOrDrag,
} from '../../shared/interactive-response-shared.hooks';
import {
  InteractiveCanvas,
  InteractiveCanvasDragZone,
  InteractiveCanvasDragZoneOuter,
  InteractiveResponsePlayContainer,
  InteractiveResponseWrapper,
  InteractiveStackDragContainer,
} from '../../shared/interactive-response-shared.style';
import {
  checkAnswers,
  returnItemToOriginalPosition,
} from '../../shared/interactive-response-shared.utils';
import {
  DROP_ITEM_SIZE,
  InteractiveSequenceAttempt,
  ViewProps,
} from '../settings/interactive-response.constants';
import { useInteractiveResponsePlay } from './interactive-response-sequence.hooks';
import * as Styled from './interactive-response-sequence.style';
import {
  initialise,
  populateAnswers,
} from './interactive-response-sequence.utils';
import { handleItemReturn } from '../../shared/interactive-response-shared.utils';
import { InteractiveSequenceDrop } from './interactive-sequence-drop';
import { useInteractiveAudioContext } from '../../shared/interactive-audio.context';
import { PStyled } from 'approot/shared/styles/typography.style';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import { grey7 } from 'approot/shared/styles/colours.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { InteractiveResponseCheck } from '../../shared/interactive-response-check-answer.style';
import { LargePillPrimaryButton } from 'approot/shared/styles/buttons/large-pill-button.style';
import {
  InteractiveResponseSelectAreaSnackbar,
  InteractiveResponseSelectItemSnackbar,
} from '../../shared/interactive-response-snackbars';

type Props = ViewProps & {
  attemptAnswers?: InteractiveResponseAnswerFragment;
  onExpandImage: (id: string, dragItems: DragItem[]) => void;
  onAnswerAttempt: InteractiveSequenceAttempt;
};

const InteractiveSequenceFree = ({
  properties,
  attemptAnswers,
  onAnswerAttempt,
  onExpandImage,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(true);
  const [checkFreeResponseAnswer, setCheckFreeResponseAnswer] =
    useState<boolean>(false);

  const sequenceProperties = properties.interactiveResponse;

  const { setAudioList } = useInteractiveAudioContext();

  const { toggleAudio, playClick, playStart, playAudio } =
    useInteractiveAudio();

  const {
    playAttempt,
    setPlayAttempt,
    dropItems,
    setDropItems,
    dragItems,
    setDragItems,
    keyboardState,
    handleOnDrop,
    handleKeyboardInteraction,
  } = useInteractiveResponsePlay({
    playClick,
    isCheckAnswerAtEnd: true,
    isFreeResponse: true,
    onAnswerAttempt: () => {
      setCheckFreeResponseAnswer(true);
    },
  });

  const {
    dropZoneSelected,
    dragItemSelected,
    handleItemReturnToStack,
    handleDroppedDragItemClick,
    handleDragItemClick,
    handleDropZoneClick,
  } = useInteractiveClickOrDrag();

  const initialiseItems = useCallback(
    (startAgain?: boolean) => {
      const { dragItems, dropItems } =
        attemptAnswers && !startAgain
          ? populateAnswers(
              attemptAnswers,
              sequenceProperties.items,
              sequenceProperties.variant
            )
          : initialise(
              sequenceProperties.variant,
              sequenceProperties.items,
              true
            );
      setDropItems(dropItems);
      setDragItems(dragItems);
      setIsTransitioning(true);
      setPlayAttempt(playAttempt + 1);
    },
    [
      sequenceProperties.items,
      sequenceProperties.variant,
      playAttempt,
      setPlayAttempt,
      setDragItems,
      setDropItems,
      setIsTransitioning,
      attemptAnswers,
    ]
  );

  const setup = useCallback(
    (startAgain?: boolean) => {
      initialiseItems(startAgain);
      setTimeout(() => {
        setIsTransitioning(false);
        playStart();
      }, 600);
    },
    [initialiseItems, playStart]
  );

  const cancelKeyboardInteraction = useCallback(() => {
    if (
      keyboardState &&
      keyboardState.action !== CANCELLED &&
      keyboardState.action !== PLACED
    ) {
      handleKeyboardInteraction({
        action: CANCELLED,
        id: keyboardState.id,
        announce: 'You have cancelled keyboard interaction',
      });
    }
  }, [keyboardState, handleKeyboardInteraction]);

  const keyboardProps = {
    target:
      keyboardState?.action === CANCELLED || keyboardState?.action === PLACED
        ? undefined
        : keyboardState?.id,
    onKeyboardInteraction: handleKeyboardInteraction,
  };

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: INTERACTIVE_DRAG_DROP,
      canDrop(item, monitor) {
        const dragItem = dragItems.find(dragItem => dragItem.id === item.id);
        if (!dragItem?.isDropped) {
          return false;
        }
        return true;
      },
      drop: (item: any) => {
        const { updatedDragItems, updatedDropItems } = handleItemReturn(
          item,
          dragItems,
          dropItems
        );
        setDragItems(updatedDragItems);
        setDropItems(updatedDropItems);
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [dragItems, dropItems]
  );

  const isEverythingAnswered = useMemo(() => {
    return isEveryDraggableDropped(dragItems);
  }, [dragItems]);

  const isInSubmittedState = isEverythingAnswered && !checkFreeResponseAnswer;

  useSortableScrollManager(ref, [INTERACTIVE_DRAG_DROP]);

  useEffect(() => {
    setup();
    //eslint-disable-next-line
  }, [properties, attemptAnswers]);

  useEffect(() => {
    setAudioList(
      sequenceProperties.items.map(item => ({
        itemId: item.itemId,
        audioUrl: item.audio?.original,
      }))
    );
  }, [setAudioList, sequenceProperties.items]);

  return (
    <InteractiveResponseWrapper>
      <InteractiveResponsePlayContainer id={INTERACTIVE_CONTAINER_ID}>
        <InteractiveCanvas id={INTERACTIVE_CANVAS_ID}>
          <InteractiveResponseTitle
            title={sequenceProperties.title}
            subTitle={sequenceProperties.subTitle}
          />

          {checkFreeResponseAnswer && (
            <InteractiveResponseCheck>
              <LargePillPrimaryButton
                type="button"
                data-selector="check-answer-button"
                onClick={() => {
                  const updatedDropItems = checkAnswers(dropItems, dragItems);
                  setCheckFreeResponseAnswer(false);
                  setDropItems([...updatedDropItems]);
                  onAnswerAttempt(updatedDropItems, dragItems);
                }}
              >
                Submit Answer
              </LargePillPrimaryButton>
            </InteractiveResponseCheck>
          )}

          {!isInSubmittedState && (
            <InteractiveCanvasDragZoneOuter>
              <InteractiveCanvasDragZone
                ref={node => {
                  drop(node);
                }}
                $isOver={isOver && canDrop}
                $isDropZoneClickable={!!dropZoneSelected}
                role="button"
                data-drop-zone-clickable="true"
                $isHighlighted={
                  !!dragItemSelected && dragItemSelected.isDropped
                }
                onClick={() => {
                  if (dragItemSelected?.isDropped) {
                    handleItemReturnToStack({
                      dragItems,
                      dropItems,
                      setDragItems,
                      setDropItems,
                    });
                  }
                }}
              >
                {dragItems.map((dragItem, index) => (
                  <InteractiveStackDragContainer
                    id={dragItem.id}
                    key={`${playAttempt}-${dragItem.id}-${index}-${playAudio}`}
                    $index={index}
                    $isTransitioning={isTransitioning}
                    $isDropped={dragItem.isDropped}
                    data-selector={`sequence-image-${index}`}
                  >
                    {!dragItem.isDropped && (
                      <InteractiveClickOrDrag
                        id={dragItem.id}
                        imageUrl={dragItem.question.image?.small}
                        isFit={dragItem.question.image?.isFit}
                        description={dragItem.question.image?.description || ''}
                        baseColour={dragItem.question.baseColour}
                        variant={dragItem.question.variant}
                        text={dragItem.question.text}
                        richtext={dragItem.question.richtext}
                        audioUrl={dragItem.question.audioUrl}
                        keyboard={keyboardProps}
                        onExpandImage={() =>
                          onExpandImage(
                            dragItem.question.image?.itemId || '',
                            dragItems
                          )
                        }
                        isDropZoneActive={!!dropZoneSelected}
                        onClick={() =>
                          handleDragItemClick({
                            dragItem,
                            handleOnDrop,
                            dropItems,
                            cancelKeyboardInteraction,
                          })
                        }
                      />
                    )}
                  </InteractiveStackDragContainer>
                ))}
              </InteractiveCanvasDragZone>
            </InteractiveCanvasDragZoneOuter>
          )}

          {isInSubmittedState && (
            <Box
              $marginBottom={24}
              $gap={4}
              $justifyContent="center"
              $alignItems="center"
            >
              <PStyled marginBottom={0} colour={grey7}>
                Your answer has been submitted.
              </PStyled>

              <LinkButton
                type="button"
                data-selector="start-over-button"
                onClick={() => setup(true)}
              >
                Change Answer
              </LinkButton>
            </Box>
          )}

          <Styled.InteractiveResponseDropItemContainer>
            {dropItems.map(dropItem => {
              const zone = dropItem.zones[0];
              const droppedDragItem = dragItems.find(
                d => d.id === zone.dragItemId || d.id === zone.previewItemId
              );

              return (
                <InteractiveSequenceDrop
                  key={`${playAttempt}-${dropItem.id}-${playAudio}`}
                  id={dropItem.id}
                  label={dropItem.label}
                  labelLength={sequenceProperties.labelLength}
                  labelBorder={sequenceProperties.labelBorder}
                  isCorrect={undefined}
                  dropItems={dropItems}
                  dragItems={dragItems}
                  onDrop={(destinationId, sourceId) => {
                    handleOnDrop(destinationId, sourceId);
                  }}
                  isContinuousMarking={false}
                  onIncorrectAnswer={element =>
                    returnItemToOriginalPosition({
                      element,
                      droppedDragItemId: droppedDragItem?.id || '',
                      scale: 1,
                      dropItem,
                      dropItems,
                      dragItems,
                      setDragItems,
                      setDropItems,
                    })
                  }
                  isDropZoneSelected={dropZoneSelected === dropItem.id}
                  isDragItemSelected={!!dragItemSelected}
                  onDropZoneClicked={
                    droppedDragItem
                      ? undefined
                      : id =>
                          handleDropZoneClick({
                            handleOnDrop,
                            id,
                            cancelKeyboardInteraction,
                          })
                  }
                >
                  {droppedDragItem && (
                    <InteractiveClickOrDrag
                      id={droppedDragItem.id}
                      imageUrl={droppedDragItem.question.image?.small}
                      isFit={droppedDragItem.question.image?.isFit}
                      description={
                        droppedDragItem.question.image?.description || ''
                      }
                      baseColour={droppedDragItem.question.baseColour}
                      variant={droppedDragItem.question.variant}
                      text={droppedDragItem.question.text}
                      richtext={droppedDragItem.question.richtext}
                      audioUrl={droppedDragItem.question.audioUrl}
                      keyboard={keyboardProps}
                      isDisabled={isInSubmittedState}
                      isCorrect={undefined}
                      droppedAt={zone.dragItemId ? dropItem.label : ''}
                      overAt={zone.previewItemId ? dropItem.label : ''}
                      scaleTo={DROP_ITEM_SIZE}
                      isDropZoneActive={!!dropZoneSelected}
                      onClick={() => {
                        // disable clicks if the answer has been submitted
                        if (isInSubmittedState) {
                          return;
                        }

                        handleDroppedDragItemClick({
                          zone,
                          droppedDragItem,
                          dragItems,
                          dropItems,
                          setDragItems,
                          setDropItems,
                          handleOnDrop,
                          cancelKeyboardInteraction,
                        });
                      }}
                    />
                  )}
                </InteractiveSequenceDrop>
              );
            })}
          </Styled.InteractiveResponseDropItemContainer>

          <InteractiveResponseScreenReader keyboardState={keyboardState} />
        </InteractiveCanvas>
      </InteractiveResponsePlayContainer>

      <InteractiveResponseHelpControls
        howToPlay={sequenceProperties.howToPlay}
        toggleAudio={toggleAudio}
        playAudio={playAudio}
      />

      {dragItemSelected && <InteractiveResponseSelectAreaSnackbar />}
      {dropZoneSelected && <InteractiveResponseSelectItemSnackbar />}
    </InteractiveResponseWrapper>
  );
};

export default InteractiveSequenceFree;
